/**
 * Flat routes for the portal. `/login` and the answer-rating page (`/avaliar#<token>`, and
 * `/avaliar/:token` for links already sent; alias `/rate`) are public; everything else is wrapped in
 * `RequireAuth` (redirect to /login when unauthenticated) and the `AppShell`.
 * `/` is the Home dashboard; the flow catalog lives at /flows. /access is a
 * company's people (its administrators); /platform/tenants is every company
 * (EVT platform administrators) — the server authorizes both. `RunView`
 * serves both /runs/:id (live/reattach) and /conversations/:id (read-only) —
 * the `readOnly` prop is the only difference.
 */
import { createBrowserRouter } from 'react-router-dom'
import { AppShell } from './app/AppShell'
import { RequireAuth } from './auth/RequireAuth'
import { AccessPage } from './pages/AccessPage'
import { ActiveRunsPage } from './pages/ActiveRunsPage'
import { ConversationsPage } from './pages/ConversationsPage'
import { FlowsPage } from './pages/FlowsPage'
import { HomePage } from './pages/HomePage'
import { LoginPage } from './pages/LoginPage'
import { MemoriesPage } from './pages/MemoriesPage'
import { NewRunPage } from './pages/NewRunPage'
import { RatePage } from './pages/RatePage'
import { RunView } from './pages/RunView'
import { TenantPage } from './pages/tenants/TenantPage'
import { TenantsPage } from './pages/tenants/TenantsPage'
import { TheaterDemoPage } from './theater/demo/TheaterDemoPage'

function Guarded({ children }: { children: React.ReactNode }) {
  return (
    <RequireAuth>
      <AppShell>{children}</AppShell>
    </RequireAuth>
  )
}

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  // Public: rating a Smith answer from the link in its e-mail (the signed token is the credential).
  // The token rides in the fragment (`/avaliar#<token>`), which no proxy logs; the path form is links already sent.
  { path: '/avaliar', element: <RatePage /> },
  { path: '/avaliar/:token', element: <RatePage /> },
  { path: '/rate', element: <RatePage /> },
  { path: '/rate/:token', element: <RatePage /> },
  // Dev-only stage for tuning the run theater without a live backend.
  ...(import.meta.env.DEV ? [{ path: '/theater-demo', element: <TheaterDemoPage /> }] : []),
  {
    path: '/',
    element: (
      <Guarded>
        <HomePage />
      </Guarded>
    )
  },
  {
    path: '/flows',
    element: (
      <Guarded>
        <FlowsPage />
      </Guarded>
    )
  },
  {
    path: '/flows/:flowId/new',
    element: (
      <Guarded>
        <NewRunPage />
      </Guarded>
    )
  },
  {
    path: '/runs/:id',
    element: (
      <Guarded>
        <RunView />
      </Guarded>
    )
  },
  {
    path: '/memories',
    element: (
      <Guarded>
        <MemoriesPage />
      </Guarded>
    )
  },
  {
    path: '/active',
    element: (
      <Guarded>
        <ActiveRunsPage />
      </Guarded>
    )
  },
  {
    path: '/conversations',
    element: (
      <Guarded>
        <ConversationsPage />
      </Guarded>
    )
  },
  {
    path: '/conversations/:id',
    element: (
      <Guarded>
        <RunView readOnly />
      </Guarded>
    )
  },
  // A company's administrators manage their own people.
  {
    path: '/access',
    element: (
      <Guarded>
        <AccessPage />
      </Guarded>
    )
  },
  // EVT platform administrators: every company.
  {
    path: '/platform/tenants',
    element: (
      <Guarded>
        <TenantsPage />
      </Guarded>
    )
  },
  {
    path: '/platform/tenants/:tenantId',
    element: (
      <Guarded>
        <TenantPage />
      </Guarded>
    )
  },
  {
    path: '*',
    element: (
      <Guarded>
        <HomePage />
      </Guarded>
    )
  }
])
